import type {ReactElement} from 'react';
import {useState} from 'react';
import type {BirthdayFormat} from '@FormMakerReact/form-maker.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {Text, TextSize} from '@Components/text/text';
import {LabelledCheckbox} from '@Components/labelled-checkbox/labelled-checkbox';
import {CHECKBOX_TYPE} from '@Components/checkbox-legacy/checkbox';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {FieldEditRowProps, FieldEditRowKind} from './field-edit-row.types';
import {BIRTHDAY_FORMAT_OPTIONS} from './field-edit-row.types';
import styles from './field-edit-row.module.scss';

export function FieldEditRow({
  rowId,
  kind,
  labelValue,
  birthdayFormat,
  isRequired,
  isOpen,
  onLabelChange,
  onFormatChange,
  onRequiredChange,
}: FieldEditRowProps): ReactElement | null {
  const [isFormatDropdownOpen, setIsFormatDropdownOpen] = useState(false);
  const labelInputId = `field-edit-row-label-input-${rowId}`;

  return (
    <div className={resolvePanelClassName(isOpen)} role="group" aria-hidden={!isOpen}>
      {renderRequiredCheckbox(isRequired, onRequiredChange)}
      {renderLabelInput(kind, labelInputId, labelValue, onLabelChange)}
      {renderFormatDropdown(kind, birthdayFormat, isFormatDropdownOpen, setIsFormatDropdownOpen, onFormatChange)}
    </div>
  );
}

// Panel stays mounted; the .editPanelOpen modifier drives the max-height + opacity transition.
function resolvePanelClassName(isOpen: boolean): string {
  return [styles.editPanel, isOpen ? styles.editPanelOpen : ''].filter(Boolean).join(' ');
}

function renderRequiredCheckbox(isRequired: boolean, onRequiredChange: (v: boolean) => void): ReactElement {
  return (
    <LabelledCheckbox
      className={styles.requiredRow}
      label={window.i18next.t('pmwjs_required')}
      isChecked={isRequired}
      onChange={onRequiredChange}
      checkboxType={CHECKBOX_TYPE.PRIMARY}
      checkboxSize={IconSize.SIZE_ICON_12}
      textSize={TextSize.MD}
    />
  );
}

function renderLabelInput(kind: FieldEditRowKind, inputId: string, labelValue: string, onLabelChange: (v: string) => void): ReactElement | null {
  if (kind !== 'custom') {
    return null;
  }

  return (
    <div className={styles.labelSection}>
      <label className={styles.labelFieldLabel} htmlFor={inputId}>
        {window.i18next.t('pmwjs_title')}
      </label>
      <input
        id={inputId}
        type="text"
        className={styles.labelInput}
        value={labelValue}
        placeholder={window.i18next.t('pmwjs_enter_field_name')}
        onChange={(e): void => {
          onLabelChange(e.target.value);
        }}
      />
    </div>
  );
}

function renderFormatDropdown(
  kind: FieldEditRowKind,
  birthdayFormat: BirthdayFormat,
  isOpen: boolean,
  setIsOpen: (open: boolean) => void,
  onFormatChange: (f: BirthdayFormat) => void
): ReactElement | null {
  if (kind !== 'birthday') {
    return null;
  }

  const selectorText = window.i18next.t(BIRTHDAY_FORMAT_OPTIONS.find((opt) => opt.value === birthdayFormat)?.labelKey ?? 'pmwjs_format_dmy');

  return (
    <div className={styles.formatSection}>
      <Text val={window.i18next.t('pmwjs_format')} size={TextSize.MD} className={styles.formatLabel} />
      <Dropdown
        popUpHasCustomWidth
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        shouldFlip
        onOpen={(): void => {
          setIsOpen(true);
        }}
        onClose={(): void => {
          setIsOpen(false);
        }}
        popup={buildFormatPopup(birthdayFormat, setIsOpen, onFormatChange)}
        selector={<DropdownGhostSelectBox size={DROPDOWN_SIZE.SMALL} text={selectorText} isOpened={isOpen} />}
      />
    </div>
  );
}

function buildFormatPopup(birthdayFormat: BirthdayFormat, setIsOpen: (open: boolean) => void, onFormatChange: (f: BirthdayFormat) => void): ReactElement {
  const items: ControlledListItemType[] = BIRTHDAY_FORMAT_OPTIONS.map(
    (opt): ControlledListItemType => ({
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
      id: opt.value,
      text: window.i18next.t(opt.labelKey),
      isSelected: opt.value === birthdayFormat,
    })
  );

  return (
    <ControlledList
      items={items}
      onClick={(id): void => {
        onFormatChange(id as BirthdayFormat);
        setIsOpen(false);
      }}
    />
  );
}
